import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function Requests({ user }) {
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [profile, setProfile] = useState(null);
  const [filters, setFilters] = useState({category:'all',urgency:'all',city:''});
  const [form, setForm] = useState({title:'',description:'',category:'groceries',urgency:'normal',city:'',postcode:'',budget_min:0,budget_max:0,is_paid:false,proposed_time:'',phone_contact:false});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(()=>{
    const load = async () => {
      const snap = await getDoc(doc(db,'users',user.uid));
      if(snap.exists()) setProfile(snap.data());
      const q = query(collection(db,'requests'),orderBy('created_at','desc'));
      const qs = await getDocs(q);
      setRequests(qs.docs.map(d=>({id:d.id,...d.data()})));
    };
    load();
  },[user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await addDoc(collection(db,'requests'),{
      ...form, requester_uid: user.uid,
      requester_name: profile?.name||user.displayName,
      requester_phone: form.phone_contact ? profile?.phone : '',
      status: 'open', volunteer_uid: null,
      created_at: serverTimestamp(),
    });
    setSuccess('Request posted successfully!');
    setShowForm(false);
    setForm({title:'',description:'',category:'groceries',urgency:'normal',city:'',postcode:'',budget_min:0,budget_max:0,is_paid:false,proposed_time:'',phone_contact:false});
    const q = query(collection(db,'requests'),orderBy('created_at','desc'));
    const qs = await getDocs(q);
    setRequests(qs.docs.map(d=>({id:d.id,...d.data()})));
    setLoading(false);
  };

  const filtered = requests.filter(r=>{
    if(filters.category!=='all' && r.category!==filters.category) return false;
    if(filters.urgency!=='all' && r.urgency!==filters.urgency) return false;
    if(filters.city && !r.city?.toLowerCase().includes(filters.city.toLowerCase())) return false;
    return true;
  });

  const urgencyColor = {urgent:'badge-red',normal:'badge-orange',low:'badge-green'};
  const categoryIcons = {groceries:'🛒',childcare:'👶',elderly:'👴',transport:'🚗',medical:'🏥',repairs:'🔧',tutoring:'📚',other:'🤝'};

  return (
    <div style={{minHeight:'100vh',background:'#F0FDF4'}}>
      <nav className="nav">
        <Link className="nav-brand" to="/"><div className="nav-logo">🏘</div><div className="nav-name">Community<span>Bridge</span></div></Link>
        <div className="nav-links">
          <Link to="/dashboard" className="nav-link">Home</Link>
          <Link to="/requests" className="nav-link active">Requests</Link>
          <Link to="/events" className="nav-link">Events</Link>
          <Link to="/volunteers" className="nav-link">Volunteers</Link>
        </div>
      </nav>
      <div className="page">
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1.5rem',flexWrap:'wrap',gap:'1rem'}}>
          <div>
            <h1 className="page-title">🆘 Help Requests</h1>
            <p className="page-sub">Browse and respond to community needs near you</p>
          </div>
          <button className="btn btn-primary" onClick={()=>setShowForm(!showForm)}>+ Post Request</button>
        </div>

        {success && <div className="alert alert-green">{success}</div>}

        {showForm && (
          <div className="card" style={{marginBottom:'1.5rem'}}>
            <h3 style={{fontSize:'18px',fontWeight:700,marginBottom:'1rem'}}>📝 New Help Request</h3>
            <form onSubmit={handleSubmit}>
              <div className="grid2">
                <div className="form-group"><label>Title</label><input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Brief title of your request" required/></div>
                <div className="form-group">
                  <label>Category</label>
                  <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                    {Object.keys(categoryIcons).map(c=><option key={c} value={c}>{categoryIcons[c]} {c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>City</label><input value={form.city} onChange={e=>setForm({...form,city:e.target.value})} placeholder="Your city"/></div>
                <div className="form-group"><label>Postcode</label><input value={form.postcode} onChange={e=>setForm({...form,postcode:e.target.value})} placeholder="e.g. 47810"/></div>
                <div className="form-group">
                  <label>Urgency</label>
                  <select value={form.urgency} onChange={e=>setForm({...form,urgency:e.target.value})}>
                    <option value="low">💚 Low — Flexible timing</option>
                    <option value="normal">🟡 Normal — Within a few days</option>
                    <option value="urgent">🔴 Urgent — ASAP</option>
                  </select>
                </div>
                <div className="form-group"><label>Preferred Time</label><input type="datetime-local" value={form.proposed_time} onChange={e=>setForm({...form,proposed_time:e.target.value})}/></div>
                <div className="form-group">
                  <label>Budget Min (RM) — 0 = Free</label>
                  <input type="number" value={form.budget_min} onChange={e=>setForm({...form,budget_min:Number(e.target.value),is_paid:Number(e.target.value)>0})} min={0}/>
                </div>
                <div className="form-group">
                  <label>Budget Max (RM)</label>
                  <input type="number" value={form.budget_max} onChange={e=>setForm({...form,budget_max:Number(e.target.value)})} min={0}/>
                </div>
              </div>
              <div className="form-group"><label>Description</label><textarea rows={3} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Describe what you need in detail..." required/></div>
              <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'1rem'}}>
                <input type="checkbox" id="phoneContact" style={{width:'auto'}} checked={form.phone_contact} onChange={e=>setForm({...form,phone_contact:e.target.checked})}/>
                <label htmlFor="phoneContact" style={{marginBottom:0}}>Share my phone number with volunteers</label>
              </div>
              <div style={{display:'flex',gap:'8px'}}>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Posting...':'Post Request'}</button>
                <button type="button" className="btn btn-gray" onClick={()=>setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div className="card" style={{marginBottom:'1.5rem',padding:'1rem'}}>
          <div style={{display:'flex',gap:'10px',flexWrap:'wrap',alignItems:'center'}}>
            <span style={{fontSize:'13px',fontWeight:600,color:'#374151'}}>🔍 Filter:</span>
            <select style={{width:'auto',padding:'6px 12px',fontSize:'13px'}} value={filters.category} onChange={e=>setFilters({...filters,category:e.target.value})}>
              <option value="all">All Categories</option>
              {Object.keys(categoryIcons).map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <select style={{width:'auto',padding:'6px 12px',fontSize:'13px'}} value={filters.urgency} onChange={e=>setFilters({...filters,urgency:e.target.value})}>
              <option value="all">All Urgency</option>
              <option value="urgent">🔴 Urgent</option>
              <option value="normal">🟡 Normal</option>
              <option value="low">💚 Low</option>
            </select>
            <input style={{width:'160px',padding:'6px 12px',fontSize:'13px'}} placeholder="Filter by city..." value={filters.city} onChange={e=>setFilters({...filters,city:e.target.value})}/>
            <span style={{fontSize:'13px',color:'#6B7280'}}>{filtered.length} results</span>
          </div>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {filtered.length===0 ? (
            <div className="card" style={{textAlign:'center',padding:'3rem',color:'#6B7280'}}>
              <div style={{fontSize:'48px',marginBottom:'12px'}}>📭</div>
              <p style={{fontWeight:600,marginBottom:'8px'}}>No requests found</p>
              <p style={{fontSize:'14px'}}>Be the first to post a help request in your community!</p>
            </div>
          ) : filtered.map(r=>(
            <div key={r.id} className="card" style={{padding:'1.25rem'}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'12px',flexWrap:'wrap'}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px',flexWrap:'wrap'}}>
                    <span style={{fontSize:'20px'}}>{categoryIcons[r.category]||'🤝'}</span>
                    <h3 style={{fontSize:'16px',fontWeight:700}}>{r.title}</h3>
                    <span className={`badge ${urgencyColor[r.urgency]||'badge-gray'}`}>{r.urgency}</span>
                    {r.is_paid && <span className="badge badge-green">💰 Paid</span>}
                  </div>
                  <p style={{fontSize:'13px',color:'#4B5563',marginBottom:'8px',lineHeight:1.5}}>{r.description}</p>
                  <div style={{display:'flex',gap:'12px',flexWrap:'wrap',fontSize:'12px',color:'#6B7280'}}>
                    <span>📍 {r.city} {r.postcode}</span>
                    <span>👤 {r.requester_name}</span>
                    {r.proposed_time && <span>📅 {new Date(r.proposed_time).toLocaleDateString()}</span>}
                    {r.budget_max>0 && <span style={{color:'#16A34A',fontWeight:600}}>💰 RM{r.budget_min}-{r.budget_max}</span>}
                    {r.requester_phone && <span>📱 {r.requester_phone}</span>}
                  </div>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:'6px',flexShrink:0}}>
                  <span className={`badge ${r.status==='open'?'badge-blue':r.status==='completed'?'badge-green':'badge-gray'}`}>{r.status}</span>
                  {r.status==='open' && r.requester_uid!==user.uid && (
                    <button className="btn btn-primary" style={{fontSize:'12px',padding:'6px 12px'}}>🤝 Volunteer</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
